import { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ReplayIcon from "@mui/icons-material/Replay";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiConstants, postGeneric } from "../../common/constants";
import ChartCard, { Segmented } from "./ChartCard";
import KpiTile from "./KpiTile";
import SortedBarChart from "./SortedBarChart";
import TrendChart from "./TrendChart";
import AgeingCard from "./AgeingCard";
import DonutCard from "./DonutCard";
import StackedShareCard from "./StackedShareCard";
import TatHealthCard from "./TatHealthCard";
import AnalyticsHero, { periodLabel } from "./AnalyticsHero";
import { Gauge } from "./MiniVisuals";
import { prettyStatus } from "./analyticsLabels";
import { ACCENTS, CARD_SX, COLORS, fmtCount, fmtDay, fmtDuration, fmtPct, freshness, pct, rise } from "./emsTheme";

// ── Data helpers ─────────────────────────────────────────────────────────────────────────────

const openOf = (r) => (r.openCount || 0) + (r.pendingCount || 0);

/** Every headline number from one stats response. */
const kpis = (s) => {
    if (!s) return null;
    const m = s.summary || {};
    const created = m.totalCount != null ? m.totalCount : (s.byCategory || []).reduce((a, r) => a + (r.totalCount || 0), 0);
    const resolvedJudged = (m.resolvedWithinTatCount || 0) + (m.resolvedBeyondTatCount || 0);
    const resolvedOrClosed = m.resolvedOrClosedCount != null ? m.resolvedOrClosedCount
        : (s.byCategory || []).reduce((a, r) => a + (r.resolvedCount || 0) + (r.closedCount || 0), 0);
    return {
        open: m.unresolvedCount ?? 0,
        beyond: m.overdueCount ?? 0,
        beyondPct: pct(m.overdueCount || 0, m.unresolvedCount || 0),
        dueToday: m.dueTodayCount ?? 0,
        resolved: m.resolvedCount ?? 0,
        unassigned: m.unassignedCount ?? 0,
        withinPct: pct(m.resolvedWithinTatCount || 0, resolvedJudged),
        resolvedJudged,
        avgResolution: m.avgResolutionMinutes ?? null,
        reopenPct: pct(m.reopenedCount || 0, resolvedOrClosed),
        reopened: m.reopenedCount || 0,
        resolvedOrClosed,
        created,
    };
};

/**
 * A running backlog that ends on the tile's own number: each day's point is the tile total minus
 * the tickets (still open / past TAT) raised after that day — i.e. "raised up to this day and
 * still open". Plotting the per-day counts instead made the line peak and fall back to 0 while
 * the tile showed the whole backlog.
 */
const backlogSpark = (daily, total, fn) => {
    if (!daily || !daily.length || total == null) return null;
    const out = new Array(daily.length);
    let after = 0;
    for (let i = daily.length - 1; i >= 0; i -= 1) {
        out[i] = { date: daily[i].date, value: Math.max(0, total - after) };
        after += fn(daily[i]) || 0;
    }
    return out;
};
/** A category or merchant name in quotes, so names like "Other" read as names inside a sentence. */
const quoted = (name) => `\u201C${name}\u201D`;
const plural = (n, one, many) => `${fmtCount(n)} ${n === 1 ? one : many}`;

// ── Copy written from the data ───────────────────────────────────────────────────────────────

const storySentence = (cur, stats) => {
    if (!cur) return "";
    if (!cur.open && !cur.created) return "No tickets match these filters for the selected period.";
    if (!cur.open) return `All ${plural(cur.created, "ticket", "tickets")} raised in this period are resolved or closed.`;
    let s = `${plural(cur.open, "ticket is", "tickets are")} open`;
    if (cur.beyond) {
        s += ` and ${fmtCount(cur.beyond)} (${fmtPct(cur.beyondPct)}) ${cur.beyond === 1 ? "is" : "are"} past TAT`;
        const worst = [...(stats.byCategory || [])].sort((a, b) => (b.overdueCount || 0) - (a.overdueCount || 0))[0];
        if (worst && worst.overdueCount) s += `. Most past-TAT tickets are in the ${quoted(worst.label)} category (${fmtCount(worst.overdueCount)})`;
    } else {
        s += " and none are past TAT yet";
        const top = [...(stats.byCategory || [])].sort((a, b) => openOf(b) - openOf(a))[0];
        if (top && openOf(top)) s += `. Most open tickets are in the ${quoted(top.label)} category (${fmtCount(openOf(top))})`;
    }
    return `${s}.`;
};

const trendTitle = (points) => {
    if (!points || !points.length) return "Tickets raised and resolved";
    if (points.length === 1) return `${plural(points[0].created, "ticket", "tickets")} raised and ${fmtCount(points[0].resolved)} resolved on ${fmtDay(points[0].date)}`;
    const behind = points.filter((p) => p.created > p.resolved).length;
    return behind ? `More tickets came in than were resolved on ${behind} of ${points.length} days` : `Resolutions kept pace with new tickets on all ${points.length} days`;
};

const topShare = (rows) => {
    const tot = rows.reduce((a, r) => a + r.value, 0);
    const top = [...rows].sort((a, b) => b.value - a.value)[0];
    return top && tot ? { top, share: Math.round((top.value * 100) / tot) } : null;
};

// ── Breakdown table ──────────────────────────────────────────────────────────────────────────

const BREAKDOWN_TABS = [
    { key: "byMerchant", label: "Merchant", filterField: "merchantId" },
    { key: "byCategory", label: "Category", filterField: "category" },
    { key: "byAssignee", label: "Assignee", filterField: "assignedToUserId" },
    { key: "byOrderStatus", label: "Order status", filterField: "orderStatuses" },
];
const rowLabel = (tabKey, row) => (tabKey === "byOrderStatus" && row.key ? prettyStatus(row.key) : row.label);

// ── Filters shown on cards (the date is shown once, in the headline) ─────────────────────────

const chipsFor = (filters, stats, bar) => {
    const f = filters || {};
    const out = [];
    if (f.merchantId) out.push((stats?.byMerchant || []).find((r) => r.key === f.merchantId)?.label || "1 merchant");
    if (f.courierNames?.length) out.push(f.courierNames.length === 1 ? f.courierNames[0] : `${f.courierNames.length} couriers`);
    if (f.category) out.push(f.category);
    if (f.subCategory) out.push(f.subCategory);
    if (f.status) out.push(f.status);
    if (f.severity) out.push(f.severity);
    if (f.orderStatuses?.length) out.push(f.orderStatuses.length === 1 ? prettyStatus(f.orderStatuses[0]) : `${f.orderStatuses.length} order statuses`);
    if (f.unassignedOnly) out.push("Unassigned");
    if (f.assignedToUserId) out.push((bar?.assigneeOptions || []).find((o) => o.value === f.assignedToUserId)?.label || "1 assignee");
    return out;
};


// ── Page focus: clicking a slice, part or name filters the whole page in place ─────────────────

/** No focus. One shared empty list, so "still no focus" never looks like a change. */
const NO_FOCUS = [];

/** Later focuses win; assignee and "unassigned" replace each other. */
const combineFocus = (focus) => focus.reduce((acc, f) => {
    const next = { ...acc, ...f.patch };
    if ("assignedToUserId" in f.patch) delete next.unassignedOnly;
    if ("unassignedOnly" in f.patch) delete next.assignedToUserId;
    return next;
}, {});

// ── Layout pieces ────────────────────────────────────────────────────────────────────────────

const Section = ({ eyebrow, title, sub, color = COLORS.purple, order = 0, children }) => (
    <Box component="section" sx={{ display: "grid", gap: 2, mt: 2.5, ...rise(order) }}>
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color }}>{eyebrow}</Typography>
            <Typography component="h2" sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: COLORS.ink, mt: 0.25, letterSpacing: "-.01em" }}>{title}</Typography>
            {sub && <Typography sx={{ fontSize: 14, color: COLORS.ink3, mt: 0.25 }}>{sub}</Typography>}
        </Box>
        {children}
    </Box>
);

/** A compact, clickable number for the secondary strip. */
const MiniStat = ({ label, value, sub, tone, onClick, info, icon: Icon, accent = "purple" }) => {
    const a = ACCENTS[accent];
    return (
        <Box component={onClick ? "button" : "div"} type={onClick ? "button" : undefined} onClick={onClick} title={info}
            aria-label={onClick ? `${label}: ${value}. Open these tickets` : undefined}
            sx={{ all: "unset", boxSizing: "border-box", display: "flex", gap: 1.5, alignItems: "flex-start", px: 2, py: 1.75, cursor: onClick ? "pointer" : "default", borderRadius: "10px",
                transition: "background-color 150ms", "&:hover": onClick ? { bgcolor: COLORS.ground } : {}, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
            {Icon && <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: a.soft, display: "grid", placeItems: "center", flexShrink: 0 }} aria-hidden><Icon sx={{ fontSize: 18, color: a.solid }} /></Box>}
            <Box sx={{ display: "grid", gap: 0.125, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.ink3 }}>{label}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: tone || COLORS.ink, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>{value}</Typography>
                {sub && <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>{sub}</Typography>}
            </Box>
        </Box>
    );
};

/** Shown in place of an empty chart when the empty state is good news. */
const AllClear = ({ title, sub }) => (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", p: 2, borderRadius: "10px", bgcolor: ACCENTS.green.soft }}>
        <CheckCircleIcon sx={{ fontSize: 32, color: ACCENTS.green.solid }} aria-hidden />
        <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.ink }}>{title}</Typography>
            {sub && <Typography sx={{ fontSize: 13, color: COLORS.ink2, mt: 0.25 }}>{sub}</Typography>}
        </Box>
    </Box>
);

/** "Updated x ago", on its own 30 s timer, so the clock doesn't re-render the whole page. */
const Freshness = ({ at }) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(t);
    }, []);
    const updated = freshness(at, now);
    return updated ? <Typography sx={{ fontSize: 12, opacity: 0.7, ml: "auto" }}>{updated}</Typography> : null;
};

/** A chip for one focus, on the dark hero. */
const FocusChip = ({ label, onRemove }) => (
    <Box component="button" type="button" onClick={onRemove} aria-label={`Remove focus ${label}`}
        sx={{ all: "unset", display: "inline-flex", alignItems: "center", gap: 0.5, pl: 1.25, pr: 0.75, py: 0.5, borderRadius: "999px", cursor: "pointer",
            bgcolor: "rgba(255,255,255,.16)", border: "1px solid rgba(255,255,255,.28)", color: "#fff", fontSize: 13, fontWeight: 600,
            "&:hover": { bgcolor: "rgba(255,255,255,.24)" }, "&:focus-visible": { outline: "2px solid #fff" } }}>
        {label}<CloseRoundedIcon sx={{ fontSize: 16 }} aria-hidden />
    </Box>
);

/**
 * Analytics overview, ordered by the decisions ops leads make. Every figure counts tickets raised
 * in the selected range (plus filters). There is no previous-period comparison: "open now" for an
 * older period always looks better, so it was misleading. Clicking a slice, part or name in a breakdown focuses the whole page on it (chips
 * in the header undo it); clicking a number, bar or heatmap cell opens those tickets.
 */
const AnalyticsSection = ({ filters, onDrilldown, bar }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [fetchedAt, setFetchedAt] = useState(null);
    const [reload, setReload] = useState(0);
    const [tatBy, setTatBy] = useState("category");
    const [tatUnit, setTatUnit] = useState("count");
    const [breakdownTab, setBreakdownTab] = useState("byMerchant");
    const retry = useCallback(() => setReload((r) => r + 1), []);

    // A new filter-bar search starts without focus. The focus is kept with the filters it was set
    // under and reads as empty once they change, worked out here during render. Resetting it in an
    // effect instead sent one stats request with the new filters and the old focus, then another.
    const [focusState, setFocusState] = useState({ forFilters: filters, items: NO_FOCUS });
    const focus = focusState.forFilters === filters ? focusState.items : NO_FOCUS;
    const setFocus = (update) => setFocusState((prev) => {
        const items = typeof update === "function" ? update(prev.forFilters === filters ? prev.items : NO_FOCUS) : update;
        return { forFilters: filters, items: items.length ? items : NO_FOCUS };
    });
    const focusPatch = useMemo(() => combineFocus(focus), [focus]);
    const effective = useMemo(() => ({ ...filters, ...focusPatch }), [filters, focusPatch]);
    const addFocus = (dim, label, patch) => setFocus((f) => [...f.filter((x) => x.dim !== dim), { dim, label, patch }]);
    const removeFocus = (dim) => setFocus((f) => f.filter((x) => x.dim !== dim));
    const drill = (patch) => onDrilldown({ ...focusPatch, ...patch });
    // For a breakdown card: the slice the page is focused on, and how to undo it, from that card.
    const focusedChip = (dim) => {
        const f = focus.find((x) => x.dim === dim);
        return f ? { label: f.label.replace(/^[^:]+:\s*/, ""), onClear: () => removeFocus(dim) } : null;
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        (async () => {
            try {
                const cur = await postGeneric(apiConstants.ESCALATION_STATS_V2, effective);
                if (cancelled) return;
                setStats(cur?.data || null);
                setFetchedAt(Date.now());
            } catch (e) {
                if (!cancelled) { setError(true); setStats(null); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [effective, reload]);

    const cur = useMemo(() => kpis(stats), [stats]);
    // Stable arrays, so the trend lines only redraw when the numbers change.
    const openSpark = useMemo(() => backlogSpark(stats?.daily, cur?.open, (d) => d.open), [stats, cur]);
    const pastSpark = useMemo(() => backlogSpark(stats?.daily, cur?.beyond, (d) => d.beyondTat), [stats, cur]);
    const chips = chipsFor(effective, stats, bar);
    const hasData = !!stats && !error;
    // Refetches after a focus keep the last numbers on screen instead of flashing skeletons.
    const cardState = { loading: loading && !stats, error, onRetry: retry, chips, empty: false };


    // Breakdown rows. Rebuilt only when the numbers change, so the charts given them don't redraw
    // on every render (a hover, a tab switch).
    const { donutCategory, donutMerchant, donutAssignee, donutOrder, trendPoints } = useMemo(() => {
        const totalOf = (r) => r.totalCount || 0;
        return {
            donutCategory: (stats?.byCategory || []).map((r) => ({ key: r.key, label: r.label, value: totalOf(r) })),
            donutMerchant: (stats?.byMerchant || []).map((r) => ({ key: r.key, label: r.label, value: totalOf(r) })),
            donutAssignee: (stats?.byAssignee || []).map((r) => ({ key: r.key || "__unassigned", userId: r.key, label: r.label || "Unassigned", value: totalOf(r) })),
            donutOrder: (stats?.byOrderStatus || []).map((r) => ({ key: r.key, label: r.key ? prettyStatus(r.key) : r.label, value: openOf(r) })),
            trendPoints: (stats?.trend || []).map((t) => ({ date: t.date, created: t.createdCount, resolved: t.resolvedCount })),
        };
    }, [stats]);
    const tatRows = useMemo(() => ((tatBy === "category" ? stats?.byCategory : stats?.byMerchant) || [])
        .map((r) => ({ key: r.key, label: r.label, value: r.overdueCount || 0, denominator: openOf(r) })).filter((r) => r.denominator), [stats, tatBy]);

    const cat = topShare(donutCategory), mer = topShare(donutMerchant), ord = topShare(donutOrder), asg = topShare(donutAssignee);
    const unassignedSlice = donutAssignee.find((r) => r.key === "__unassigned");
    const tatTop = [...tatRows].sort((a, b) => b.value - a.value)[0];
    const tatWord = tatBy === "category" ? "categories" : "merchants";
    const allPast = tatRows.filter((r) => r.denominator && r.value === r.denominator);
    const tatTopPct = [...tatRows].sort((a, b) => pct(b.value, b.denominator) - pct(a.value, a.denominator) || b.denominator - a.denominator)[0];
    const tatTitle = !cur?.beyond || !tatTop ? "No open ticket is past TAT"
        : tatUnit === "pct"
            ? (allPast.length > 1 ? `${allPast.length} ${tatWord} have every open ticket past TAT`
                : `${quoted(tatTopPct.label)} has the highest share past TAT, ${fmtPct(pct(tatTopPct.value, tatTopPct.denominator), 0)} (${fmtCount(tatTopPct.value)} of ${fmtCount(tatTopPct.denominator)} open)`)
            : `${quoted(tatTop.label)} has the most tickets past TAT (${fmtCount(tatTop.value)} of ${fmtCount(tatTop.denominator)} open)`;
    const tab = BREAKDOWN_TABS.find((t) => t.key === breakdownTab);
    const tabRows = stats?.[breakdownTab] || [];
    const allClear = hasData && !cur?.beyond;

    // What a click in each breakdown focuses on.
    const focusCategory = (r) => r.key && addFocus("category", `Category: ${r.label}`, { category: r.key });
    const focusMerchant = (r) => r.key && addFocus("merchant", `Merchant: ${r.label}`, { merchantId: r.key });
    const focusOrder = (r) => r.key && addFocus("order", `Order status: ${r.label}`, { orderStatuses: [r.key] });
    const focusAssignee = (r) => addFocus("assignee", `Assignee: ${r.label}`, r.userId ? { assignedToUserId: r.userId } : { unassignedOnly: true });
    const focusRow = (tabKey, r) => ({
        byMerchant: () => focusMerchant(r),
        byCategory: () => focusCategory(r),
        byAssignee: () => focusAssignee({ ...r, userId: r.key, label: r.label || "Unassigned" }),
        byOrderStatus: () => focusOrder({ key: r.key, label: rowLabel(tabKey, r) }),
    }[tabKey]());

    return (
        <Box sx={{ display: "grid", gap: 2.5 }}>
            {/* Hero: the period, the one-sentence story and the page focus; the headline numbers float over its lower edge */}
            <AnalyticsHero
                pill={periodLabel(effective)}
                story={loading && !stats ? "Working out the numbers…" : error ? "Couldn't load analytics for these filters." : storySentence(cur, stats)}
                footer={(
                    <>
                        {focus.length ? (
                            <>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>Focused on</Typography>
                                {focus.map((f) => <FocusChip key={f.dim} label={f.label} onRemove={() => removeFocus(f.dim)} />)}
                                <Box component="button" type="button" onClick={() => setFocus([])}
                                    sx={{ all: "unset", fontSize: 13, fontWeight: 600, cursor: "pointer", textDecoration: "underline", opacity: 0.85, "&:hover": { opacity: 1 }, "&:focus-visible": { outline: "2px solid #fff" } }}>Clear</Box>
                                <Box component="button" type="button" onClick={() => drill({})}
                                    sx={{ all: "unset", display: "inline-flex", alignItems: "center", gap: 0.5, ml: { sm: 1 }, px: 1.5, py: 0.6, borderRadius: "999px", cursor: "pointer",
                                        bgcolor: "#fff", color: COLORS.purple, fontSize: 13, fontWeight: 700, boxShadow: "0 4px 14px rgba(0,0,0,.18)",
                                        "&:hover": { bgcolor: "#F6F2FB" }, "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 } }}>
                                    View these tickets<ArrowForwardIcon sx={{ fontSize: 16 }} aria-hidden />
                                </Box>
                            </>
                        ) : (
                            <Typography sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 13, opacity: 0.85 }}>
                                <TouchAppIcon sx={{ fontSize: 16 }} aria-hidden />Click any slice or name below to focus the whole page on it.
                            </Typography>
                        )}
                        {!loading && <Freshness at={fetchedAt} />}
                    </>
                )}
                tiles={(
                    <>
                        <KpiTile loading={loading && !stats} label="Open backlog" value={cur?.open} format={fmtCount} polarity="lower" accent="purple" icon={Inventory2OutlinedIcon}
                            caption="Open and pending tickets raised in this period"
                            info="Tickets raised in the selected range that are open or pending now. Date basis: ticket created date."
                            spark={openSpark} sparkLabel="Open, raised up to this day"
                            onClick={() => drill({ unresolvedOnly: true })} sx={rise(1)} />
                        <KpiTile loading={loading && !stats} label="Past TAT" value={cur?.beyond} format={fmtCount} polarity="lower" accent="red" icon={AlarmOnIcon}
                            secondary={cur?.open ? `${fmtPct(cur.beyondPct)} of open` : null}
                            caption="Open tickets already past their resolution TAT"
                            info="Open or pending tickets whose resolution due time has passed, as a share of open tickets. TAT is set per subcategory (48 hours if none)."
                            spark={pastSpark} sparkLabel="Past TAT, raised up to this day"
                            onClick={() => drill({ overdueOnly: true })} sx={rise(2)} />
                        <KpiTile loading={loading && !stats} label="Resolved within TAT" value={cur?.resolvedJudged ? cur.withinPct : "None resolved yet"} format={(v) => fmtPct(v)} polarity="higher"
                            accent="green" icon={VerifiedOutlinedIcon}
                            secondary={cur?.resolvedJudged ? `of ${fmtCount(cur.resolvedJudged)} resolved` : null}
                            visual={cur?.resolvedJudged ? <Box sx={{ pb: 1.5 }}><Gauge value={cur.withinPct} color={ACCENTS.green.solid} showValue={false} label={`${fmtPct(cur.withinPct)} resolved within TAT`} /></Box> : null}
                            caption="Resolved tickets closed on or before their TAT"
                            info="Resolved on or before the resolution due time, divided by all resolved tickets with a due time (weighted by tickets)."
                            onClick={() => drill({ closedOnly: true })} sx={rise(3)} />
                        <KpiTile loading={loading && !stats} label="Resolution time" polarity="lower" accent="blue" icon={TimerOutlinedIcon}
                            value={cur?.avgResolution != null ? fmtDuration(cur.avgResolution) : "None resolved yet"}
                            caption="Average time from raised to resolved"
                            info="Average time from raised to resolved, for tickets raised in the range that have been resolved. Calendar hours." sx={rise(4)} />
                    </>
                )}
            />

            {/* While focused, a bar that stays in view on scroll: what is focused and how to undo it. */}
            {focus.length > 0 && (
                <Box sx={{ position: "sticky", top: { xs: 60, sm: 68 }, zIndex: 6, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                    px: 2, py: 1.25, borderRadius: "12px", background: ACCENTS.purple.grad, color: "#fff", boxShadow: "0 8px 24px rgba(76,46,112,.28)" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, opacity: 0.9 }}>Focused on</Typography>
                    {focus.map((f) => <FocusChip key={f.dim} label={f.label} onRemove={() => removeFocus(f.dim)} />)}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}>
                        <Box component="button" type="button" onClick={() => setFocus([])}
                            sx={{ all: "unset", fontSize: 13, fontWeight: 700, cursor: "pointer", px: 1.25, py: 0.5, borderRadius: "999px", border: "1px solid rgba(255,255,255,.5)",
                                "&:hover": { bgcolor: "rgba(255,255,255,.14)" }, "&:focus-visible": { outline: "2px solid #fff" } }}>
                            Show all data
                        </Box>
                        <Box component="button" type="button" onClick={() => drill({})}
                            sx={{ all: "unset", display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: "999px", cursor: "pointer",
                                bgcolor: "#fff", color: COLORS.purple, fontSize: 13, fontWeight: 700, "&:hover": { bgcolor: "#F6F2FB" }, "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 } }}>
                            View these tickets<ArrowForwardIcon sx={{ fontSize: 16 }} aria-hidden />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* The rest of the numbers */}
            <Box sx={{ ...CARD_SX, p: 0.75, display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" }, ...rise(5) }}>
                <MiniStat label="Raised" icon={AddCircleOutlineIcon} accent="purple" value={loading && !stats ? "…" : fmtCount(cur?.created)}
                    sub="in the selected range" onClick={() => drill({})} info="Tickets raised in the selected range" />
                <MiniStat label="Resolved" icon={TaskAltIcon} accent="green" value={loading && !stats ? "…" : fmtCount(cur?.resolved)} onClick={() => drill({ statusCategory: "RESOLVED" })} info="Tickets raised in the range that are now Resolved" />
                <MiniStat label="Due today" icon={ScheduleIcon} accent="orange" value={loading && !stats ? "…" : fmtCount(cur?.dueToday)} tone={cur?.dueToday ? COLORS.orangeText : undefined}
                    sub={cur?.dueToday ? "TAT ends today" : "nothing due today"} onClick={() => drill({ dueTodayOnly: true })} info="Open tickets whose TAT ends today" />
                <MiniStat label="Reopened" icon={ReplayIcon} accent="blue" value={loading && !stats ? "…" : fmtCount(cur?.reopened)}
                    sub={cur?.resolvedOrClosed ? `${fmtPct(cur.reopenPct)} of resolved or closed` : null}
                    onClick={() => drill({ reopenedOnly: true })} info="Tickets reopened at any point" />
                <MiniStat label="Unassigned" icon={PersonOffIcon} accent="red" value={loading && !stats ? "…" : fmtCount(cur?.unassigned)} tone={cur?.unassigned ? COLORS.orangeText : undefined}
                    sub={cur?.unassigned ? "open with no owner" : "every ticket has an owner"} onClick={() => drill({ unresolvedOnly: true, unassignedOnly: true })} info="Open or pending tickets with nobody assigned" />
            </Box>

            {/* Breakdowns at the top (feedback point 4) */}
            <Section eyebrow="Breakdown" title="Where the tickets are" sub="Click a slice or a name to focus the whole page on it" order={6}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                    <DonutCard eyebrow="By category" cardState={cardState} rows={donutCategory} fileName="ems-by-category"
                        title={cat ? `${cat.top.label} is ${cat.share}% of tickets` : "Tickets by category"}
                        info="Tickets raised in the range, by category." onSelect={focusCategory} focused={focusedChip("category")} />
                    <DonutCard eyebrow="By merchant" cardState={cardState} rows={donutMerchant} fileName="ems-by-merchant"
                        title={mer ? `${mer.top.label} raised ${mer.share}% of tickets` : "Tickets by merchant"}
                        info="Tickets raised in the range, by merchant." onSelect={focusMerchant} focused={focusedChip("merchant")} />
                    <StackedShareCard eyebrow="Open tickets by order status" cardState={cardState} rows={donutOrder} unitLabel="open" fileName="ems-open-by-order-status"
                        title={ord ? `${ord.share}% of open tickets are ${ord.top.label}` : "Open tickets by order status"}
                        info="Open or pending tickets by their AWB's current order status, refreshed every 5 minutes."
                        onSelect={focusOrder} focused={focusedChip("order")} />
                    {stats?.byAssignee != null && (
                        <StackedShareCard eyebrow="By assignee" cardState={cardState} rows={donutAssignee} fileName="ems-by-assignee"
                            title={unassignedSlice ? `${plural(unassignedSlice.value, "ticket has", "tickets have")} no assignee` : asg ? `${asg.top.label} holds ${asg.share}% of tickets` : "Tickets by assignee"}
                            info="Tickets raised in the range, by current assignee."
                            onSelect={focusAssignee} focused={focusedChip("assignee")} />
                    )}
                </Box>
            </Section>

            {/* TAT (feedback point 12) */}
            <Section eyebrow="TAT" color={ACCENTS.red.solid} title="Are tickets resolved on time?" sub="In TAT vs out of TAT, and where the misses are. Click a bar to open those tickets." order={7}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 5fr) minmax(0, 7fr)" }, gap: 2, alignItems: "start" }}>
                    <TatHealthCard summary={stats?.summary} cardState={cardState} onDrilldown={drill} />
                    <ChartCard {...cardState} eyebrow={`Past TAT by ${tatBy}`} title={tatTitle} empty={hasData && !tatRows.length && !allClear} emptyHint="No open tickets."
                        info="Open or pending tickets past their resolution due time. % is past TAT divided by that row's open tickets."
                        fileName={`ems-past-tat-by-${tatBy}`} height={allClear ? 120 : 240}
                        actions={allClear ? null : (
                            <>
                                <Segmented label="Group by" value={tatBy} onChange={setTatBy} options={[{ value: "category", label: "Category" }, ...(stats?.byMerchant?.length ? [{ value: "merchant", label: "Merchant" }] : [])]} />
                                <Segmented label="Count or percentage" value={tatUnit} onChange={setTatUnit} options={[{ value: "count", label: "Count" }, { value: "pct", label: "%" }]} />
                            </>
                        )}
                        table={allClear ? null : { columns: [{ key: "label", label: tatBy === "category" ? "Category" : "Merchant" }, { key: "value", label: "Past TAT", align: "right", text: (r) => fmtCount(r.value) }, { key: "denominator", label: "Open", align: "right", text: (r) => fmtCount(r.denominator) }, { key: "p", label: "% past TAT", align: "right", text: (r) => fmtPct(pct(r.value, r.denominator)) }], rows: tatRows }}>
                        {allClear ? (
                            <AllClear title={cur?.open ? `All ${plural(cur.open, "open ticket is", "open tickets are")} within TAT` : "Nothing is open"}
                                sub={cur?.dueToday ? `${plural(cur.dueToday, "ticket turns", "tickets turn")} past TAT today if not resolved.` : "Nothing is due today either."} />
                        ) : (
                            <SortedBarChart rows={tatRows} unit={tatUnit} focusTop denominatorLabel="open tickets" ariaLabel={tatTitle}
                                onSelect={(r) => drill({ [tatBy === "category" ? "category" : "merchantId"]: r.key, overdueOnly: true })} />
                        )}
                    </ChartCard>
                </Box>
            </Section>

            {/* Volume and ageing (feedback point 11) */}
            <Section eyebrow="Volume and ageing" color={ACCENTS.blue.solid} title="Is the backlog under control?" sub="New tickets against resolutions, and how long the open ones have waited" order={8}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                    <ChartCard {...cardState} eyebrow="Raised vs resolved" empty={hasData && !trendPoints.length} title={trendTitle(trendPoints)}
                        info="Raised: tickets created that day. Resolved: tickets whose resolved time falls that day. IST days."
                        partialNote={trendPoints.length ? "Today is still in progress, so it is shown lighter." : null}
                        fileName="ems-raised-vs-resolved" ariaSummary={trendTitle(trendPoints)}
                        table={{ columns: [{ key: "date", label: "Day", text: (r) => fmtDay(r.date, true) }, { key: "created", label: "Raised", align: "right", text: (r) => fmtCount(r.created) }, { key: "resolved", label: "Resolved", align: "right", text: (r) => fmtCount(r.resolved) }], rows: trendPoints }}>
                        <TrendChart points={trendPoints} />
                    </ChartCard>
                    <AgeingCard stats={stats} {...cardState} onDrilldown={drill} />
                </Box>
            </Section>

            {/* Exact numbers */}
            <Section eyebrow="Details" color={COLORS.ink2} title="Full breakdown" sub="Open + Pending + Resolved + Closed = Total. Past TAT is the part of Open + Pending past its TAT. Click a name to focus the page on it." order={8}>
                <ChartCard {...cardState} eyebrow={`By ${tab.label.toLowerCase()}`}
                    title={`${tabRows.length} ${tab.label.toLowerCase()}${tabRows.length === 1 ? "" : "s"}`}
                    empty={hasData && !tabRows.length} fileName={`ems-breakdown-${breakdownTab}`}
                    actions={<Segmented label="Breakdown by" value={breakdownTab} onChange={setBreakdownTab} options={BREAKDOWN_TABS.filter((t) => stats?.[t.key] != null).map((t) => ({ value: t.key, label: t.label }))} />}
                    table={{
                        columns: [{ key: "label", label: tab.label, text: (r) => rowLabel(breakdownTab, r) },
                            ...["openCount", "pendingCount", "resolvedCount", "closedCount", "totalCount", "overdueCount"].map((k, i) => ({ key: k, align: "right", label: ["Open", "Pending", "Resolved", "Closed", "Total", "Past TAT"][i], text: (r) => fmtCount(r[k] || 0) }))],
                        rows: tabRows,
                    }}>
                    <Box sx={{ overflow: "auto", maxHeight: 420 }}>
                        <Box component="table" sx={{ width: "100%", minWidth: 560, borderCollapse: "collapse", fontSize: 13,
                            "& th": { position: "sticky", top: 0, zIndex: 1, bgcolor: COLORS.surface, textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.ink3, py: 1, px: 1.25, borderBottom: `1px solid ${COLORS.grid}` },
                            "& th:first-of-type, & td:first-of-type": { textAlign: "left" },
                            "& td": { textAlign: "right", py: 1, px: 1.25, borderBottom: `1px solid ${COLORS.grid}`, color: COLORS.ink, fontVariantNumeric: "tabular-nums" },
                            "& tbody tr:hover td": { bgcolor: COLORS.ground } }}>
                            <thead><tr><th>{tab.label}</th><th>Open</th><th>Pending</th><th>Resolved</th><th>Closed</th><th>Total</th><th>Past TAT</th></tr></thead>
                            <tbody>{tabRows.map((r) => {
                                const canFocus = breakdownTab === "byAssignee" || !!r.key;
                                return (
                                    <tr key={r.key || r.label}>
                                        <td>{canFocus ? <Box component="button" type="button" onClick={() => focusRow(breakdownTab, r)} title="Focus the page on this" sx={{ all: "unset", cursor: "pointer", color: COLORS.purple, fontWeight: 600, "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>{rowLabel(breakdownTab, r)}</Box> : rowLabel(breakdownTab, r)}</td>
                                        <td>{fmtCount(r.openCount || 0)}</td><td>{fmtCount(r.pendingCount || 0)}</td><td>{fmtCount(r.resolvedCount || 0)}</td><td>{fmtCount(r.closedCount || 0)}</td>
                                        <td style={{ fontWeight: 700 }}>{fmtCount(r.totalCount || 0)}</td>
                                        <td style={{ color: r.overdueCount ? COLORS.unfavourable : COLORS.ink3, fontWeight: r.overdueCount ? 600 : 400 }}>{fmtCount(r.overdueCount || 0)}</td>
                                    </tr>
                                );
                            })}</tbody>
                        </Box>
                    </Box>
                </ChartCard>
            </Section>

            <Accordion disableGutters sx={{ border: `1px solid ${COLORS.grid}`, borderRadius: "12px !important", boxShadow: "none", mt: 1, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>How these numbers are worked out</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box component="dl" sx={{ m: 0, display: "grid", gap: 1.25, fontSize: 13, color: COLORS.ink2, maxWidth: 820, "& dt": { fontWeight: 600, color: COLORS.ink }, "& dd": { m: 0 } }}>
                        <dt>Which tickets are counted?</dt><dd>Tickets raised in the selected date range (the last 30 days unless you change it), with the filters and any focus applied.</dd>
                        <dt>Focus</dt><dd>Clicking a slice, part or name filters the whole page to it. The chips at the top show what is focused; remove a chip or click Clear to go back. View these tickets opens the ticket list with the same filters.</dd>
                        <dt>What is TAT?</dt><dd>The resolution time set for the ticket's subcategory in the Categories tab, counted from when it was raised. 48 hours if none is set.</dd>
                        <dt>Past TAT</dt><dd>Open or pending tickets whose resolution due time has passed. Due today means the TAT ends today.</dd>
                        <dt>Resolved within TAT</dt><dd>Resolved on or before the due time, divided by all resolved tickets with a due time. Rates are weighted by tickets, never averaged.</dd>
                        <dt>Reopened</dt><dd>Tickets reopened at any point. The rate divides by tickets that are resolved or closed.</dd>
                        <dt>Resolution time</dt><dd>The average time from raised to resolved. Calendar hours, IST.</dd>
                        <dt>Colours</dt><dd>Red marks tickets past TAT, green resolved on time. Orange marks the one bar to look at first.</dd>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default AnalyticsSection;
